
import { toast } from "sonner";
import { performAISearch } from "./AISearchStrategy";
import { performBasicSearch } from "./BasicSearchStrategy";
import { performSeasonalSearch } from "./SeasonalSearchStrategy";
import { performEnhancedFallbackSearch, performBroadSearch } from "./FallbackSearchStrategy";
import { performDutchAccommodationSearch } from "./DutchAccommodationSearch";
import { detectSeasonalIntent } from '../../../supabase/functions/ai-vacation-search/seasonalIntelligence';

export const executeSearchStrategy = async (
  query: string,
  setSearchResults: (results: any[]) => void
) => {
  try {
    console.log(`=== Starting search strategy for: "${query}" ===`);
    
    // Check for seasonal search first
    console.log('Step 0: Checking for seasonal search...');
    const seasonalIntent = detectSeasonalIntent(query);
    if (seasonalIntent.isSeasonalQuery) {
      console.log('Detected seasonal query, using seasonal search strategy');
      const seasonalResults = await performSeasonalSearch(query);
      if (seasonalResults.length > 0) {
        console.log(`Seasonal search successful: ${seasonalResults.length} results found`);
        setSearchResults(seasonalResults);
        
        // Create a more specific success message based on the seasonal intent
        let message = `${seasonalResults.length} vakanties gevonden!`;
        if (seasonalIntent.month && seasonalIntent.weatherPreference === 'warm') {
          if (seasonalIntent.geographicRestrictions?.includes('outside_europe')) {
            message = `${seasonalResults.length} warme bestemmingen buiten Europa gevonden voor ${seasonalIntent.month}!`;
          } else {
            message = `${seasonalResults.length} warme bestemmingen gevonden voor ${seasonalIntent.month}!`;
          }
        } else if (seasonalIntent.weatherPreference === 'warm') {
          if (seasonalIntent.geographicRestrictions?.includes('outside_europe')) {
            message = `${seasonalResults.length} zonnige vakanties buiten Europa gevonden!`;
          } else {
            message = `${seasonalResults.length} zonnige vakanties gevonden!`;
          }
        }
        
        toast.success(message);
        return;
      }
    }
    
    // Check for Dutch accommodation search
    console.log('Step 1: Checking for Dutch accommodation search...');
    const dutchResults = await performDutchAccommodationSearch(query);
    if (dutchResults.length > 0) {
      console.log(`Dutch accommodation search successful: ${dutchResults.length} results found`);
      setSearchResults(dutchResults);
      toast.success(`${dutchResults.length} Nederlandse accommodaties gevonden!`);
      return;
    }

    // Try AI search
    console.log('Step 2: Trying AI search...');
    const aiResults = await performAISearch(query);
    if (aiResults.length > 0) {
      console.log(`AI search successful: ${aiResults.length} results found`);
      setSearchResults(aiResults);
      toast.success(`${aiResults.length} vakanties gevonden!`);
      return;
    }

    // Fallback to basic search
    console.log('Step 3: AI search empty, trying basic search...');
    const basicResults = await performBasicSearch(query);
    if (basicResults.length > 0) {
      console.log(`Basic search successful: ${basicResults.length} results found`);
      setSearchResults(basicResults);
      toast.success(`${basicResults.length} vakanties gevonden!`);
      return;
    }

    // Try enhanced fallback search with smart messaging
    console.log('Step 4: Basic search empty, trying enhanced fallback search...');
    await handleFallbackSearch(query, setSearchResults);
  } catch (error: any) {
    console.error('Search strategy execution error:', error);
    await handleFallbackSearch(query, setSearchResults);
  }
};

const handleFallbackSearch = async (query: string, setSearchResults: (results: any[]) => void) => {
  try {
    const enhancedResults = await performEnhancedFallbackSearch(query);
    if (enhancedResults.length > 0) {
      console.log(`Enhanced fallback search successful: ${enhancedResults.length} results found`);
      setSearchResults(enhancedResults);
      toast.success(`${enhancedResults.length} vakanties gevonden!`);
    } else {
      console.log('Enhanced fallback search empty, checking for specific cases...');
      
      // Check if this is a specific location/experience that we don't offer
      const lowerQuery = query.toLowerCase();
      const isSpecificLocationSearch = 
        lowerQuery.includes('afrika') || 
        lowerQuery.includes('safari door') ||
        lowerQuery.includes('australië') ||
        lowerQuery.includes('nieuw-zeeland') ||
        lowerQuery.includes('japan') ||
        lowerQuery.includes('china') ||
        lowerQuery.includes('brazilië') ||
        lowerQuery.includes('argentinië');
      
      if (isSpecificLocationSearch) {
        toast.error(`Deze bestemming zit momenteel niet in ons aanbod. Probeer eens een andere bestemming!`);
        setSearchResults([]);
        return;
      }

      // Check if this is a budget-related search that has no results
      const hasBudgetTerms = 
        lowerQuery.includes('max') || 
        lowerQuery.includes('maximaal') ||
        lowerQuery.includes('budget') ||
        lowerQuery.includes('€') ||
        lowerQuery.includes('euro') ||
        /\d+\s*(pp|per persoon)/.test(lowerQuery);
      
      if (hasBudgetTerms) {
        toast.error(`Geen vakanties gevonden binnen dit budget. Probeer een hoger budget of andere criteria!`);
        setSearchResults([]);
        return;
      }

      // Check if this is a specific vacation type search with no results
      const hasSpecificType = 
        lowerQuery.includes('zonvakantie') ||
        lowerQuery.includes('zonnige') ||
        lowerQuery.includes('all inclusive') ||
        lowerQuery.includes('adults only') ||
        lowerQuery.includes('rondreis') ||
        lowerQuery.includes('stedentrip') ||
        lowerQuery.includes('wintersport');
      
      if (hasSpecificType) {
        toast.error(`Geen vakanties gevonden voor deze specifieke criteria. Probeer andere opties!`);
        setSearchResults([]);
        return;
      }

      // Only do broad search for generic queries
      console.log('Step 5: Trying broad search as last resort...');
      const broadResults = await performBroadSearch(query);
      if (broadResults.length > 0) {
        console.log(`Broad search successful: ${broadResults.length} results found`);
        setSearchResults(broadResults);
        toast.info(`Geen exacte match gevonden. Hier zijn enkele alternatieven (${broadResults.length} vakanties)`);
      } else {
        console.log('All search strategies failed - no results found');
        toast.error("Geen vakanties gevonden voor deze zoekopdracht");
        setSearchResults([]);
      }
    }
  } catch (error: any) {
    console.error('Fallback search error:', error);
    toast.error("Er ging iets mis bij het zoeken");
    setSearchResults([]);
  }
};
